import { create } from "zustand";

type QueuedUpload = {
  file: File;
  dstPath: string;
  as?: "json" | "text" | "binary";
  sensitive?: boolean;
  contentType?: string;
};

type Selection = { path: string; type: "file" | "dir" };

type FlowFSSlice = {
  prefix: string;
  recursive: boolean;
  limit: number;
  cursor?: string;
  selection?: Selection;
  previewAs: "json" | "text";
  denyPrefixes: Set<string>;
  uploadQueue: QueuedUpload[];
  etagByPath: Map<string, string>;
  // actions
  setPrefix: (p: string) => void;
  setRecursive: (v: boolean) => void;
  setLimit: (n: number) => void;
  setCursor: (c?: string) => void;
  select: (sel?: Selection) => void;
  setPreviewAs: (as: "json" | "text") => void;
  setEtag: (path: string, etag?: string) => void;
  clearEtag: (path: string) => void;
  canEnter: (path: string) => boolean;
  markDenied: (prefix: string) => void;
};

export const useFlowFSStore = create<FlowFSSlice>((set, get) => ({
  prefix: "/",
  recursive: false,
  limit: 100,
  cursor: undefined,
  selection: undefined,
  previewAs: "json",
  denyPrefixes: new Set(),
  uploadQueue: [],
  etagByPath: new Map(),
  setPrefix: (p) => set({ prefix: p, cursor: undefined }),
  setRecursive: (v) => set({ recursive: v, cursor: undefined }),
  setLimit: (n) => set({ limit: n, cursor: undefined }),
  setCursor: (c) => set({ cursor: c }),
  select: (s) => set({ selection: s }),
  setPreviewAs: (as) => set({ previewAs: as }),
  setEtag: (path, etag) =>
    set((s) => {
      const m = new Map(s.etagByPath);
      if (etag) m.set(path, etag);
      else m.delete(path);
      return { etagByPath: m };
    }),
  clearEtag: (path) => set((s) => {
    const m = new Map(s.etagByPath);
    m.delete(path);
    return { etagByPath: m };
  }),
  canEnter: (path) => path.startsWith("/") && !path.includes("..") && !get().denyPrefixes.has(path),
  markDenied: (prefix) =>
    set((s) => {
      if (s.denyPrefixes.has(prefix)) return s;
      const next = new Set(s.denyPrefixes);
      next.add(prefix);
      return { denyPrefixes: next };
    }),
}));
